import type { ApiError } from "@entrilia/shared/api/types";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import type { Validator } from "@entrilia/shared/utilities/validators";
import { Box } from "@mui/material";
import type { ReactNode } from "react";
import EditableTitle from "../../common/EditableTitle";
import { toTypographicName } from "./assistantNames";

interface Props {
  name: string;
  validator: Validator<string>;
  tooltip: string;
  placeholder: string;
  width: string;
  logTag: string;
  children?: ReactNode;
  onRename: (name: string) => Promise<ApiError | undefined>;
  onRenamed: () => Promise<void>;
}

const AssistantNameEditor = ({
  name,
  validator,
  tooltip,
  placeholder,
  width,
  logTag,
  children,
  onRename,
  onRenamed,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const handleTitleChanged = async (title: string) => {
    const typographicName = toTypographicName(title);
    if (typographicName === name) {
      return;
    }

    const error = await onRename(typographicName);
    if (error) {
      logError(error, logTag);
      sendNotificationError(error.message);
      throw error;
    }

    await onRenamed();
  };

  return (
    <Box sx={{ width }}>
      <EditableTitle
        title={name}
        tooltip={tooltip}
        placeholder={placeholder}
        validator={validator}
        onTitleChanged={handleTitleChanged}
      >
        {children}
      </EditableTitle>
    </Box>
  );
};

export default AssistantNameEditor;
